export type Classification = 'Confidential' | 'Restricted' | 'General';
export type MarketSegment = 'Timepieces' | 'Art' | 'Real Estate' | 'Wine & Spirits' | 'Automobiles' | 'Jewellery';

export interface IntelligenceReport {
  id: string;
  title: string;
  subtitle: string;
  teaser: string;
  classification: Classification;
  segment: MarketSegment;
  publishedDate: string;
  readTime: number;
  valueImpact: string;
  valueDirection: 'positive' | 'negative' | 'neutral';
  valuePercent: number;
  image: string;
  alt: string;
  isSaved: boolean;
  isNew: boolean;
  analyst: string;
}

export const intelligenceReports: IntelligenceReport[] = [
{
  id: 'report-001',
  title: 'The Patek Philippe Secondary Market Repricing',
  subtitle: 'Four reference groups outpacing supply demand — and three where the exit window is closing',
  teaser: 'Following the CHF 4.2M sale of reference 2499 at Antiquorum Geneva, the pre-1970 complication market has entered a structural repricing cycle not seen since 2011.',
  classification: 'Restricted',
  segment: 'Timepieces',
  publishedDate: '14 Aug 2026',
  readTime: 22,
  valueImpact: '+18.4%',
  valueDirection: 'positive',
  valuePercent: 18.4,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bac840d2-1772903969337.png",
  alt: 'Luxury Patek Philippe timepiece with intricate gold dial and complications on dark velvet display',
  isSaved: true,
  isNew: true,
  analyst: 'C. Hartmann'
},
{
  id: 'report-002',
  title: 'Basquiat Private Sales: The Invisible Market',
  subtitle: 'An estimated $340M in Basquiat works has changed hands privately since 2023 — outside the auction record',
  teaser: 'The public auction market for Basquiat represents less than 40% of actual transaction volume. Three major collectors have been consolidating quietly, and one is preparing a significant private deaccession.',
  classification: 'Confidential',
  segment: 'Art',
  publishedDate: '11 Aug 2026',
  readTime: 19,
  valueImpact: 'Structural',
  valueDirection: 'neutral',
  valuePercent: 0,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18ab39334-1772064904874.png",
  alt: 'Contemporary art gallery with large-format paintings on white walls under dramatic gallery lighting',
  isSaved: false,
  isNew: true,
  analyst: 'P. Vernet'
},
{
  id: 'report-003',
  title: 'Côte d\'Azur Private Villas: The 2026 Correction',
  subtitle: 'Asking prices down 14% from 2024 peaks — why the patient buyer has a 9-month window',
  teaser: 'Seven properties above €25M on the Cap d\'Antibes and Cap Ferrat have been quietly repriced since January. The correction is real, structural, and unlikely to reverse before Q2 2027.',
  classification: 'Restricted',
  segment: 'Real Estate',
  publishedDate: '8 Aug 2026',
  readTime: 16,
  valueImpact: '-14.2%',
  valueDirection: 'negative',
  valuePercent: -14.2,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c9fd0dc3-1772622671317.png",
  alt: 'Luxurious Mediterranean villa with infinity pool overlooking the sea on the Côte d\'Azur at golden hour',
  isSaved: false,
  isNew: false,
  analyst: 'S. Beaumont'
},
{
  id: 'report-004',
  title: 'Pétrus & The Pomerol Succession Crisis',
  subtitle: 'The Moueix family succession is creating uncertainty in Pétrus allocation — and a secondary market opportunity',
  teaser: 'The Moueix family\'s third-generation transition has created structural uncertainty in Pétrus allocation channels. Three major négociants have already reduced their committed volumes. The 2020 and 2021 vintages are likely the last at current secondary prices.',
  classification: 'Confidential',
  segment: 'Wine & Spirits',
  publishedDate: '5 Aug 2026',
  readTime: 14,
  valueImpact: '+31.0%',
  valueDirection: 'positive',
  valuePercent: 31,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_107a0033e-1772055158527.png",
  alt: 'Rare Bordeaux wine bottles with dusty labels aging in stone cellar with dramatic candlelight',
  isSaved: true,
  isNew: false,
  analyst: 'A. Rousseau'
},
{
  id: 'report-005',
  title: 'The Ferrari 250 GTO Ownership Map',
  subtitle: '36 cars. 29 known owners. A provenance analysis of every confirmed GTO transaction since 1962',
  teaser: 'We have constructed the most comprehensive ownership map of the Ferrari 250 GTO ever assembled outside of Ferrari\'s own records. It reveals three cars whose provenance has been misrepresented in recent auction catalogues.',
  classification: 'Confidential',
  segment: 'Automobiles',
  publishedDate: '2 Aug 2026',
  readTime: 28,
  valueImpact: 'Ownership Intelligence',
  valueDirection: 'neutral',
  valuePercent: 0,
  image: "https://images.unsplash.com/photo-1734638352720-6edec38434c8",
  alt: 'Classic Italian sports car with curved red bodywork on empty mountain road at dusk',
  isSaved: false,
  isNew: false,
  analyst: 'R. Castellano'
},
{
  id: 'report-006',
  title: 'Kashmir Sapphire: The Supply Collapse',
  subtitle: 'No significant Kashmir sapphire mining has occurred since 1887 — and the collector market has not fully priced this',
  teaser: 'The Padder Valley mines have been effectively exhausted since the late 19th century. Yet the premium commanded by certified Kashmir-origin sapphires has only recently begun to reflect true supply scarcity. The repricing has 3–5 years to run.',
  classification: 'General',
  segment: 'Jewellery',
  publishedDate: '29 Jul 2026',
  readTime: 12,
  valueImpact: '+47.0%',
  valueDirection: 'positive',
  valuePercent: 47,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cfca0f3e-1764688967327.png",
  alt: 'Deep blue sapphire gemstone with brilliant cut facets reflecting light on dark velvet background',
  isSaved: false,
  isNew: false,
  analyst: 'I. Moreau-Delacroix'
},
{
  id: 'report-007',
  title: 'The Whisky Cask Allocation Window',
  subtitle: 'Three Scottish distilleries closing new cask programs in September — and why the 36-month return profile is exceptional',
  teaser: 'Springbank, GlenDronach, and one undisclosed Highland distillery are closing their direct cask programs to new investors in September 2026. The 36-month return on casks from these three programs has averaged 38.7% — significantly above comparable alternatives.',
  classification: 'Restricted',
  segment: 'Wine & Spirits',
  publishedDate: '25 Jul 2026',
  readTime: 15,
  valueImpact: '+38.7%',
  valueDirection: 'positive',
  valuePercent: 38.7,
  image: "https://images.unsplash.com/photo-1603971729146-58cc08a42e76",
  alt: 'Row of aged whisky barrels in dark stone warehouse cellar with amber light filtering through wooden slats',
  isSaved: false,
  isNew: false,
  analyst: 'D. MacPherson'
},
{
  id: 'report-008',
  title: 'Emerging Chinese Collectors and the Old Master Market',
  subtitle: 'A new generation of Chinese collectors is entering the Old Master market — with significant implications for pricing in 2027',
  teaser: 'Our analysis of private sale data from three major European dealers reveals a pattern of Chinese collector acquisitions in the Dutch Golden Age category that has not yet been reflected in auction results. The entry point is closing.',
  classification: 'General',
  segment: 'Art',
  publishedDate: '20 Jul 2026',
  readTime: 11,
  valueImpact: 'Emerging Trend',
  valueDirection: 'positive',
  valuePercent: 12,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_19717be1e-1773342155793.png",
  alt: 'Old Master oil painting of Dutch interior scene in ornate gilded frame in auction house setting',
  isSaved: false,
  isNew: false,
  analyst: 'L. van den Berg'
}];


export const marketSegmentData = [
{ name: 'Timepieces', value: 8, fill: '#C9A84C' },
{ name: 'Art', value: 7, fill: '#8AABCC' },
{ name: 'Real Estate', value: 6, fill: '#9B8EC4' },
{ name: 'Wine & Spirits', value: 5, fill: '#C47A7A' },
{ name: 'Automobiles', value: 4, fill: '#7AC47A' },
{ name: 'Jewellery', value: 4, fill: '#C4A77A' }];